import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ErrorBoundary from './ErrorBoundary';
import {
  Home, ClipboardList, Sun, Moon, TrendingUp, PlusCircle, Compass, User,
  FlaskConical, BookOpen, Calendar, BarChart2, AlertTriangle, ChevronRight, Award, Clock, Users, PlayCircle,
  GraduationCap,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useTour } from '../tour/TourContext';
import Logo from './Logo';
import BannerBar from './BannerBar';

// ─── App info modal ───────────────────────────────────────────────────────────
const APP_INFO_SLIDES = [
  {
    color: '#6366F1',
    icon: FlaskConical,
    title: 'Behaviour coaching, not just trading',
    text: 'TradeReflex is built to coach your behaviour. Most traders fail not because of strategy — but because of discipline. This app helps you understand yourself before you enter real markets.',
  },
  {
    color: '#10b981',
    icon: Calendar,
    title: 'Real NSE data. 35 days back.',
    text: '__NSE_DATE_EXAMPLE__',
  },
  {
    color: '#06b6d4',
    icon: Clock,
    title: 'Live market hours. Real execution.',
    text: 'The sim day runs minute-by-minute — 9:15 AM to 3:30 PM. If the real clock shows 10:30 AM, the sim is also at 10:30 AM on its date. The same candle that formed on that sim day forms here at the same second. Place equity, algo, futures, and index trades — executed against real price action. Outside market hours, weekends, and sim-date holidays, no trades can be placed. Exactly the same rules as the real market.',
  },
  {
    color: '#f59e0b',
    icon: BookOpen,
    title: 'Know yourself before the real market',
    text: 'Real traders know their edge, their biases, and their risk tolerance. TradeReflex tracks your patterns, scores your behaviour, and builds that self-awareness — safely, with no real money at risk.',
  },
  {
    color: '#8b5cf6',
    icon: BarChart2,
    title: 'Every tool a real trader uses',
    text: 'Charts, technical analysis, backtesting, algo flows, options scanner, trade orders — all powered by real market data. Experience the full market environment, trade your edge with virtual money, and zero real trades involved.',
  },
  {
    color: '#ef4444',
    icon: Users,
    title: 'The SEBI numbers no one talks about',
    text: 'SEBI\'s study found 93% of individual F&O traders lost money across FY22–FY24. Aggregate losses crossed ₹1.8 lakh crore. For intraday equity, 71% of retail traders lost money — rising to 80% among the most active ones.',
  },
  {
    color: '#f97316',
    icon: AlertTriangle,
    title: 'It\'s not bad luck — it\'s process',
    text: 'It\'s trading without a tested plan, chasing tips, and over-committing on losses. Even a 70% win-rate strategy means losing 3 out of every 10 trades. Most people stake everything on those 3 and quit before their edge ever shows up.',
    tagline: 'Be consistent. Be disciplined. Stay in long enough to find your edge.',
  },
];

function AppInfoModal({ onClose, navigate }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { requestAnimationFrame(() => setVisible(true)); }, []);

  const fmt = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const todayDate = new Date();
  const simDate   = new Date(todayDate - 35 * 86400000);
  const nseText   =
    `The sim date is always ~35 days behind today. Every price, chart, scanner result, and order execution in this app reflects that exact date — pulled from real NSE data.\n\n` +
    `Example: today is ${fmt(todayDate)}. The sim is running on ${fmt(simDate)}. The NIFTY chart you see, the scanner signals that fire, the candles that form minute-by-minute — all of it is exactly what happened on ${fmt(simDate)}. Real opens, real highs, real lows. You are trading a real market day, just 35 days in the past.\n\n` +
    `You get a genuine market experience — with zero real money at risk.`;

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 320);
  };

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: `rgba(0,0,0,${visible ? 0.72 : 0})`,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        transition: 'background 0.3s ease',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 480,
          borderRadius: '24px 24px 0 0',
          background: '#13141a',
          border: '1px solid rgba(255,255,255,0.1)',
          borderBottom: 'none',
          maxHeight: '90vh', overflowY: 'auto',
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.35s cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {/* Handle bar */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 16px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.18)' }} />
        </div>

        {/* Header */}
        <div style={{ padding: '0 20px 20px' }}>
          <p style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6366F1', marginBottom: 5 }}>
            TradeReflex
          </p>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: '#eceef5', letterSpacing: '-0.02em', lineHeight: 1.2, marginBottom: 6 }}>
            Train your mind first.
          </h2>
          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.42)', lineHeight: 1.5 }}>
            TradeReflex is a behaviour coaching app — not a trading platform. Here's what that means.
          </p>
        </div>

        {/* Info tiles */}
        <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {APP_INFO_SLIDES.map(({ color, icon: Icon, title, text: rawText, path, tagline }) => {
            const text = rawText === '__NSE_DATE_EXAMPLE__' ? nseText : rawText;
            const Wrapper = path ? 'button' : 'div';
            return (
              <Wrapper
                key={title}
                onClick={path ? () => { handleClose(); setTimeout(() => navigate(path), 320); } : undefined}
                style={{
                  width: '100%', textAlign: 'left',
                  borderRadius: 16,
                  background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
                  border: `1px solid ${path ? `${color}40` : `${color}25`}`,
                  padding: '14px',
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  cursor: path ? 'pointer' : 'default',
                }}
              >
                <div style={{
                  width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                  background: `${color}20`, border: `1px solid ${color}35`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon size={15} color={color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#eceef5', marginBottom: 3 }}>{title}</p>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', lineHeight: 1.55 }}>
                    {text.split('\n\n').map((para, i) => (
                      <p key={i} style={{ margin: i > 0 ? '6px 0 0' : 0 }}>{para}</p>
                    ))}
                  </div>
                  {tagline && (
                    <p style={{ fontSize: 12, fontWeight: 700, color, marginTop: 10, letterSpacing: '-0.01em' }}>
                      — {tagline}
                    </p>
                  )}
                  {path && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color }}>Go to Learning</span>
                      <ChevronRight size={12} color={color} />
                    </div>
                  )}
                </div>
              </Wrapper>
            );
          })}
        </div>

        {/* Disclaimer */}
        <div style={{
          margin: '16px 16px 0',
          borderRadius: 14,
          background: 'rgba(251,191,36,0.06)',
          border: '1px solid rgba(251,191,36,0.18)',
          padding: '12px 14px',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <AlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', lineHeight: 1.65 }}>
            <span style={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>For educational purposes only. </span>
            Practice here does not guarantee real-market profits. This app is purely for behaviour training and self-awareness. Real investments require your own complete analysis. TradeReflex does not promote any strategy, trade, or recommendation — always act at your own risk in real markets.{' '}
            <span style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>We're here to help new traders and those who've experienced losses understand their edge and plan accordingly.</span>
          </p>
        </div>

        {/* CTA */}
        <div style={{ padding: '16px 16px 40px' }}>
          <button
            onClick={handleClose}
            style={{
              width: '100%', padding: '14px', borderRadius: 14,
              background: 'linear-gradient(135deg, #6366F122 0%, #6366F10e 100%)',
              border: '1px solid #6366F145',
              fontSize: 14, fontWeight: 800, color: '#6366F1',
              cursor: 'pointer',
            }}
          >
            Got it, let's trade
          </button>
        </div>
      </div>
    </div>
  );
}

const navItems = [
  { path: '/',          icon: Home,          label: 'Home',     tourId: 'nav-home'     },
  { path: '/discover',  icon: Compass,       label: 'Discover', tourId: 'nav-discover' },
  { path: '/trade',     icon: TrendingUp,    label: 'Trade',    tourId: 'nav-trade'    },
  { path: '/orders',    icon: ClipboardList, label: 'Orders',   tourId: 'nav-orders'   },
  { path: '/profile',   icon: User,          label: 'Profile',  tourId: 'nav-profile'  },
];

function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme, colors } = useTheme();
  const { user } = useAuth();
  const { openLauncher } = useTour();
  const [showAppInfo, setShowAppInfo] = useState(false);

  // Auto-show once per day on first load
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem('app_popup_date') !== today) setShowAppInfo(true);
  }, []);

  // Allow any child to open the modal via custom event
  useEffect(() => {
    const handler = () => setShowAppInfo(true);
    window.addEventListener('open-app-info', handler);
    return () => window.removeEventListener('open-app-info', handler);
  }, []);

  const closeAppInfo = () => {
    localStorage.setItem('app_popup_date', new Date().toISOString().slice(0, 10));
    setShowAppInfo(false);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Top Bar */}
      <div
        className="backdrop-blur-xl border-b sticky top-0 z-50"
        style={{
          backgroundColor: theme === 'light' ? 'rgba(255,255,255,0.94)' : 'rgba(13,14,20,0.88)',
          borderColor: theme === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.07)',
          boxShadow: theme === 'light' ? '0 1px 0 rgba(0,0,0,0.06)' : '0 1px 0 rgba(255,255,255,0.04)',
          paddingTop: 'env(safe-area-inset-top)',
          overflow: 'visible',
        }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between h-12 overflow-visible">
            <div className="flex items-center gap-2">
              <Link to="/" className="flex items-center" style={{ color: colors.primaryText }}>
                <Logo size="md" />
              </Link>
            </div>

            <div className="flex items-center gap-2">
              {/* Strategy Feed */}
              <button
                onClick={() => navigate('/forum')}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 34, height: 34, borderRadius: 10,
                  background: 'rgba(99,102,241,0.08)',
                  border: '1px solid rgba(99,102,241,0.18)',
                  cursor: 'pointer',
                }}
                aria-label="Strategy Feed"
              >
                <Users style={{ width: 15, height: 15, color: '#818cf8' }} />
              </button>
              {/* Mentorship */}
              <button
                onClick={() => navigate('/mentorship')}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 34, height: 34, borderRadius: 10,
                  background: 'rgba(99,102,241,0.08)',
                  border: '1px solid rgba(99,102,241,0.18)',
                  cursor: 'pointer',
                }}
                aria-label="Mentorship"
              >
                <GraduationCap style={{ width: 15, height: 15, color: '#818cf8' }} />
              </button>
              {/* Virtual Balance */}
              {user?.virtual_balance != null && (
                <button
                  onClick={() => navigate('/capital')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '5px 10px', borderRadius: 10,
                    background: 'rgba(22,199,132,0.1)',
                    border: '1px solid rgba(22,199,132,0.22)',
                    cursor: 'pointer',
                  }}
                >
                  <PlusCircle style={{ width: 13, height: 13, flexShrink: 0, color: '#16C784' }} />
                  <span className="hidden sm:inline" style={{ fontSize: 12, fontWeight: 700, color: '#16C784', fontVariantNumeric: 'tabular-nums' }}>
                    ₹{Number(user.virtual_balance).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </span>
                  <span className="sm:hidden" style={{ fontSize: 12, fontWeight: 700, color: '#16C784', fontVariantNumeric: 'tabular-nums' }}>
                    ₹{(Number(user.virtual_balance) / 1000).toFixed(0)}K
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <BannerBar />

      {/* Bottom Navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50"
        style={{
          backgroundColor: theme === 'light' ? '#1E1B4B' : 'rgba(13,14,20,0.92)',
          borderTop: theme === 'light' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(255,255,255,0.07)',
          backdropFilter: 'blur(16px)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="max-w-7xl mx-auto px-2">
          <div className="flex items-center justify-around h-14">
            {navItems.map((item) => {
              const Icon = item.icon;
              const DISCOVER_PATHS = ['/analytics', '/markets', '/derivatives', '/global-cues',
                '/algo-flows', '/backtest', '/journey', '/learning', '/docs', '/ai-coach', '/act',
                '/calculator', '/appguide', '/scanners', '/screener'];
              const isActive = location.pathname === item.path ||
                (item.path === '/profile'  && location.pathname.startsWith('/profile')) ||
                (item.path === '/discover' && DISCOVER_PATHS.some(p => location.pathname.startsWith(p)));
              const activeColor = theme === 'light' ? '#FFFFFF' : (colors.accent || '#6366F1');
              const inactiveColor = theme === 'light' ? 'rgba(255,255,255,0.45)' : colors.secondaryText;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-tour={item.tourId}
                  aria-label={item.label}
                  aria-current={isActive ? 'page' : undefined}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    gap: 3, minWidth: 52, padding: '6px 8px',
                    borderRadius: 12,
                    color: isActive ? activeColor : inactiveColor,
                    background: isActive
                      ? (theme === 'light' ? 'rgba(255,255,255,0.12)' : 'rgba(99,102,241,0.12)')
                      : 'transparent',
                    transition: 'all 0.15s',
                    textDecoration: 'none',
                  }}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  onClick={
                    (item.path === '/discover' && location.pathname === '/discover') ||
                    (item.path === '/profile'  && location.pathname === '/profile')
                      ? (e) => { e.preventDefault(); navigate(item.path, { replace: true }); }
                      : undefined
                  }
                >
                  <Icon style={{ width: 18, height: 18 }} strokeWidth={isActive ? 2.2 : 1.8} />
                  <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, letterSpacing: isActive ? '0.01em' : 0 }}>{item.label}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 pb-20">
        <ErrorBoundary inline message="This page failed to load. Tap Retry or go back.">
          {children}
        </ErrorBoundary>
      </main>

      {showAppInfo && <AppInfoModal onClose={closeAppInfo} navigate={navigate} />}
    </div>
  );
}

export default Layout;
